import * as H from "history";
import * as _ from "lodash";
import moment from "moment";
import {
    CommandBarButton,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    MessageBarType,
    PrimaryButton,
    Toggle
} from "office-ui-fabric-react";
import React, {Component, Fragment, ReactElement} from 'react';
import { connect } from "react-redux";
import {
    DurableFunctionInstanceHelpers,
    GroundFloorDurableFunctionInstance
} from "../../../../models/DurableFunctionInstance";
import Actions from "../../../../redux/Actions";
import { IRootState } from "../../../../store";
import DispatchProps from "../../../../utils/DispatchProps";
import { openFileDownload, unfriendlyDurableFunctionInstanceName, unfriendlySchoolName } from "../../../../utils/Utils";
import ApiService from "../../../ApiService";
import DurableFunctionHistoryEventList from "../../../DurableFunctionHistoryEventList";
import GroundFloorDurableFunctionInstanceList from "../../../GroundFloorDurableFunctionInstanceList";
import WorkUnitInvocationLogList from "../../../WorkUnitInvocationLogList";
import {DurableFunctionHistoryEvent} from "../../../../models/DurableFunctionHistoryEvent";
import WorkloadOutputViewer from "../../../WorkloadOutputViewer";
import {buildIndexedSchools, IndexedSchools} from "../../../../utils/IndexedSchools";
import School from "../../../../models/School";
import Table, {TableColumn} from "../../../Table";
import TableCell from "../../../TableCell";
import BulkWorkloadItem from "../../../../models/BulkWorkloadItem";
import {UserAccessLevel, userAccessLevelIsAtLeast} from "../../../../models/UserAccessRecord";
import SchoolPageTitle from "../../../SchoolPageTitle";
import AdminSchoolMenu from "../../../AdminSchoolMenu";

interface GroundFloorDurableFunctionInstancePageProps {
    instanceId: string;
    apiService: ApiService;
    usersCdbAccessLevel?: UserAccessLevel;
    history: H.History;
}

interface GroundFloorDurableFunctionInstancePageState {
    showTerminateDialog: boolean;
    indexedSchools?: IndexedSchools;
    instance?: GroundFloorDurableFunctionInstance;
    instanceIsPlaylistOrBulkWorkload: boolean;
    instanceIsChild: boolean;
    instanceHistory: DurableFunctionHistoryEvent[];
    childWorkloadInstances?: GroundFloorDurableFunctionInstance[];
    statusCheckIntervalId?: number;
    statusLastCheckedAt?: string;
    showingOutput: boolean;
    bulkItems?: BulkWorkloadItem[];
    filteredBulkItems?: BulkWorkloadItem[];
    bulkItemsHideCompleted: boolean;
    triggeredStartAnotherTenantIds: string[];
    excludedSchoolCodes: string[];
}

class GroundFloorDurableFunctionInstancePage extends Component<GroundFloorDurableFunctionInstancePageProps & DispatchProps ,GroundFloorDurableFunctionInstancePageState> {
    static displayName = GroundFloorDurableFunctionInstancePage.name;

    constructor(props: GroundFloorDurableFunctionInstancePageProps & DispatchProps) {
        super(props);

        this.state = {
            showTerminateDialog: false,
            instance: undefined,
            instanceIsPlaylistOrBulkWorkload: false,
            instanceIsChild: false,
            instanceHistory: [],
            childWorkloadInstances: undefined,
            statusCheckIntervalId: undefined,
            statusLastCheckedAt: undefined,
            showingOutput: false,
            bulkItems: undefined,
            filteredBulkItems: undefined,
            bulkItemsHideCompleted: false,
            triggeredStartAnotherTenantIds: [],
            excludedSchoolCodes: []
        };
    }

    fetchInstanceAndRelatedData = async (instanceId: string): Promise<GroundFloorDurableFunctionInstance> => {
        const instance = await this.props.apiService.getGroundFloorDurableFunctionInstance(instanceId);
        const instanceHistory = await this.props.apiService.getWorkloadsFunctionInstanceHistory(instanceId);
        
        let schools: School[] = [];
        let instanceType = DurableFunctionInstanceHelpers.GetDurableFunctionInstanceType(instance);
        let bulkItems: BulkWorkloadItem[] | undefined = undefined;
        let excludedSchoolCodes: string[] = [];

        if(instanceType === "BulkWorkload")
        {
            schools = await this.props.apiService.getSchoolsAdmin(true,true,false,[],[],""); // filter relevant to bulk in future
            bulkItems = await this.props.apiService.getBulkWorkloadItems(this.props.instanceId);
            excludedSchoolCodes = bulkItems.filter(item => item.isExcluded).map(item => item.schoolCode);
        }
        else
        {
            const school = await this.props.apiService.getSchoolAdmin(instance.input.tenantId,instance.input.schoolCode);
            schools = [school];
        }
        
        
        this.setState({
            instance: instance,
            instanceHistory: instanceHistory,
            indexedSchools: buildIndexedSchools(schools),
            bulkItems: bulkItems,
            filteredBulkItems: bulkItems,
            excludedSchoolCodes: excludedSchoolCodes
        });
        
        return instance;
    }
    
    async componentDidMount() {
        if(this.props.instanceId)
        {
            await this.fetchInstanceAndRelatedData(this.props.instanceId);
        }
    }

    async componentDidUpdate(prevProps: Readonly<GroundFloorDurableFunctionInstancePageProps & DispatchProps>, prevState: any): Promise<void> {

        if (this.props.instanceId !== prevProps.instanceId)
        {
            await this.fetchInstanceAndRelatedData(this.props.instanceId);
        }

        if (this.state.instance && this.state.instance !== prevState.instance)
        {
            const instanceIsPlaylistOrBulkWorkload = !!this.state.instance.playlistName || !!this.state.instance.bulkWorkloadName;
            
            let childWorkloadInstances: GroundFloorDurableFunctionInstance[] = [];
            if (instanceIsPlaylistOrBulkWorkload)
            {
                
                childWorkloadInstances = await this.props.apiService.getGroundFloorDurableFunctionInstanceChildren(this.props.instanceId);
    
                childWorkloadInstances = _.sortBy(childWorkloadInstances,i => parseInt(_.split(i.durableFunctionInstanceId,':')[1]));
            }
            
            const instanceIsChild = this.props.instanceId ? _.includes(this.props.instanceId,":") : false;
            
            this.setState({
                instanceIsPlaylistOrBulkWorkload,
                instanceIsChild,
                childWorkloadInstances
            });
        }
    }
    
    private terminateInstance = async () => {
        
        try
        {
            await this.props.apiService.terminateGroundFloorDurableFunctionInstance(this.state.instance!.durableFunctionInstanceId);
            await this.fetchInstanceAndRelatedData(this.props.instanceId);
            await this.props.dispatch(Actions.showMessageBar("Successfully terminated workload",MessageBarType.success));
        }
        catch(e)
        {
            await this.props.dispatch(Actions.showMessageBar(`Failed to terminate workload : ${e.message}`,MessageBarType.error));
        }

        this.setState({
            showTerminateDialog: false,
        });
        
    };
    
    private buildTerminateButton() : JSX.Element | undefined {
        
        if(this.state.instance!.runtimeStatus === "Running" || this.state.instance!.runtimeStatus === "Pending")
        {
            return (
                <CommandBarButton
                    iconProps={{iconName: "Cancel"}}
                    text="Terminate"
                    onClick={() => {
                        this.setState({
                            showTerminateDialog: true
                        })
                    }}
                />
            );
        }
        else
        {
            return undefined;
        }

    }

    private restartPlaylist = async () => {
        try{
            await this.props.apiService.restartPlaylist(this.state.instance!.durableFunctionInstanceId);
            await this.fetchInstanceAndRelatedData(this.props.instanceId);
            await this.props.dispatch(Actions.showMessageBar("Sucessfully Restarted the playlist from last failed workload", MessageBarType.success));
        }
        catch (e) {
            await this.props.dispatch(Actions.showMessageBar(`Failed to restart the playlist from last failed workload : ${e.message}`, MessageBarType.error));
        }
    }

    private buildRestartButton() : JSX.Element | undefined {
        return (
            <CommandBarButton
                iconProps={{ iconName: "Rewind" }}
                text="Restart From Failed"
                onClick={this.restartPlaylist}   
            />
        );
    }

    private buildGoToParentButton() : JSX.Element | undefined {
        return (
            <CommandBarButton
                iconProps={{ iconName: "NumberedList" }}
                text="Go to parent"
                onClick={() => {
                    
                    const instanceIdParts = _.split(this.props.instanceId,":");

                    let parentPartition = "";
                    if(instanceIdParts.length >= 3 )
                    {
                        parentPartition = instanceIdParts[1];
                    }
                    
                    const parentId = `${parentPartition}${instanceIdParts[0]}`;
                    this.props.history.push(`/cdb-admin/workloads/${parentId}`);
                }}
            />
        );
    }

    private NotifyUser = async () => {

        console.log(`Checking status for : ${this.props.instanceId} , statusCheckIntervalId : ${this.state.statusCheckIntervalId}`)

        this.setState({
            statusLastCheckedAt: moment().format('HH:mm:ss')
        })
        
        const updatedInstance = await this.fetchInstanceAndRelatedData(this.props.instanceId);
        let instanceType = DurableFunctionInstanceHelpers.GetDurableFunctionInstanceType(updatedInstance);

        if(updatedInstance.runtimeStatus === "Terminated")
        {
            console.log(`${instanceType} terminated stopping loop`)
            clearInterval(this.state.statusCheckIntervalId);
        }
        else if (updatedInstance.runtimeStatus === "Completed" || updatedInstance.runtimeStatus === "Failed")
        {
            if (instanceType === "Playlist")
            {
                new Notification(`${instanceType} ` + updatedInstance.runtimeStatus, { body: updatedInstance.playlistName + " has " + updatedInstance.runtimeStatus.toLowerCase() + " at " + updatedInstance.tenantName + " for " + updatedInstance.schoolCode });
            }
            else if (instanceType === "BulkWorkload")
            {
                new Notification(`${instanceType} ` + updatedInstance.runtimeStatus, { body: "Bulk " + updatedInstance.bulkWorkloadName + " has " + updatedInstance.runtimeStatus.toLowerCase() });
            }
            else
            {
                new Notification(`${instanceType} ` + updatedInstance.runtimeStatus, { body: updatedInstance.workloadName + " has " + updatedInstance.runtimeStatus.toLowerCase() + " at " + updatedInstance.tenantName + " for " + updatedInstance.schoolCode });
            }
            console.log("Notification Sent")
            clearInterval(this.state.statusCheckIntervalId);
        }
        
    }

    private StartLoop = () => {

        if (Notification.permission === "granted") {
            const statusCheckIntervalId = setInterval(this.NotifyUser, 30000) as any;

            this.props.dispatch(Actions.showMessageBar(`Successfully watching. Please keep this tab open and do not refresh`, MessageBarType.success));

            this.setState({
                statusCheckIntervalId: statusCheckIntervalId,
            });
        }
        else if (Notification.permission === "denied") {
            console.log("Notification permisison denied")
        }
        else {
            console.log("Requesting notification permisison")
            Notification.requestPermission();
        }
    }
    
    private buildWatchButton(): JSX.Element | undefined {

        let text: string = "Watch Status (Preview)";

        if(this.state.statusCheckIntervalId)
        {
            if(this.state.statusLastCheckedAt)
            {
                text = "Status Last Checked : " + this.state.statusLastCheckedAt;
            }    
            else
            {
                text = "Watching";
            }
        }
        
        return (
            <CommandBarButton
                iconProps={{ iconName: "RedEye" }}
                text={text}
                disabled={this.state.statusCheckIntervalId ? true : false}
                onClick={this.StartLoop}
                />
            );
    }
    
    private buildShowOutputButton(): JSX.Element | undefined {
        
        if(this.state.showingOutput || !this.state.instance?.Output)
        {
            return undefined;
        }

        return (
            <CommandBarButton
                iconProps={{ iconName: "OpenFile" }}
                text={"Show Output"}
                onClick={() => this.setState({showingOutput: true})}
            />
        );
    }
    
    private buildOutputViewer(title: string,output: any,openFlagProperty: string) : JSX.Element | undefined
    {
        const open: boolean = (this.state as any)[openFlagProperty];
        
        if(!open || !output)
        {
            return undefined;
        }

        let outputObject = undefined;
        
        if(typeof output === "string")
        {
            outputObject = JSON.parse(output);
        }
        else
        {
            outputObject = output;
        }
        
        return (
            <WorkloadOutputViewer title={title} jsonObject={outputObject}>
                <CommandBarButton
                    iconProps={{iconName: "Cancel"}}
                    text="Close"
                    onClick={() => {
                        const stateUpdate: any = {};
                        stateUpdate[openFlagProperty] = false;
                        
                        this.setState(stateUpdate);
                    }}
                />
            </WorkloadOutputViewer>
        );
    }
    
    private buildStartAnotherWithFailedButton(): JSX.Element | undefined
    {
        if(!this.state.instance?.bulkWorkloadName)
        {
            return undefined;
        }

        return (
            <CommandBarButton
                text={"Start another with failed (preview)"}
                iconProps={{iconName: "Rerun"}}
                onClick={async () => {
                    try {
                        let instance = await this.props.apiService.bulkWorkloadStartAnotherWithFailed(this.props.instanceId);
                        this.props.history.push(`/cdb-admin/workloads/${instance.durableFunctionInstanceId}`);
                    }
                    catch(e) {
                        this.props.dispatch(Actions.showMessageBar(e.message, MessageBarType.error));
                    }
                }}/>
        );
    }
    
    private buildHeaderButtons()
    {
        return(
            <Fragment>
                {this.buildStartAnotherWithFailedButton()}
                {this.state.instance?.runtimeStatus === "Completed" ? this.buildShowOutputButton(): null}
                {this.state.instanceIsChild ? this.buildGoToParentButton() : null}
                {this.state.instance?.failedAndAwaitingRestart ? this.buildRestartButton() : null}
                {this.state.instance?.runtimeStatus === "Running" || this.state.instance?.runtimeStatus === "Pending" ? this.buildWatchButton() : null}
                {this.state.instance?.runtimeStatus === "Running" || this.state.instance?.runtimeStatus === "Pending" ? this.buildTerminateButton() : null}
            </Fragment>
        );
    }
    
    private buildBulkItemTable(): JSX.Element | undefined {
        if (this.state.bulkItems === undefined || this.state.filteredBulkItems === undefined) {
            return undefined;
        }

        const columns: TableColumn[] = [
            {
                title: "Tenant Id",
                propertyPath: "tenantId",
                flex: 1,
                enableSort: true
            },
            {
                title: "Tenant Name",
                propertyPath: "tenantName",
                flex: 1,
                enableSort: true
            },
            {
                title: "School Code",
                propertyPath: "schoolCode",
                flex: 1,
                enableSort: true
            },
            {
                title: "Current Workload Id",
                propertyPath: "currentWorkloadInstanceId",
                flex: 1,
                enableSort: true
            },
            {
                title: "Current Workload Status",
                propertyPath: "currentWorkloadInstanceRuntimeStatus",
                flex: 1,
                enableSort: true
            },
            {
                title: "",
                propertyPath: "actions",
                flex: 1
            }
        ];

        const cellForColumn = (item: BulkWorkloadItem, column: TableColumn, columnIndex: number): ReactElement<TableCell> | undefined => {
            switch (column.propertyPath) {
                case "actions":
                    
                    let beginButton = null;
                    if(item.isStartedPaused)
                    {
                        beginButton = (
                            <CommandBarButton
                                iconProps={{ iconName: "Start" }}
                                text="Begin (tenant)"
                                onClick={async () => {
                                    await this.props.apiService.bulkWorkloadItemBegin(item.runBulkItemInstanceId);
                                    await this.props.dispatch(Actions.showMessageBar(`Sent begin event for ${item.tenantId}`, MessageBarType.success));
                                }}
                            />
                        )
                    }
                    
                    let failedActions = null;
                    if (item.currentWorkloadInstanceRuntimeStatus === "Failed")
                    {
                        var disabled = this.state.triggeredStartAnotherTenantIds.some(i => i === item.tenantId) ||
                            this.state.bulkItems?.some(i => i.isCurrent && i.tenantId === item.tenantId) ||
                            this.state.excludedSchoolCodes.some(i => i === item.schoolCode);
                        failedActions = (
                            <Fragment>
                                <CommandBarButton
                                    disabled={disabled}                                 
                                    iconProps={{ iconName: "Rerun" }}
                                    text="Start another"
                                    onClick={async () => {
                                        this.state.triggeredStartAnotherTenantIds.push(item.tenantId);
                                        this.setState({ triggeredStartAnotherTenantIds: this.state.triggeredStartAnotherTenantIds })
                                        this.props.apiService.bulkWorkloadItemStartAnother(item.runBulkItemInstanceId, item.schoolCode);
                                        await this.props.dispatch(Actions.showMessageBar(`Restarted for ${item.schoolCode}`, MessageBarType.success));
                                    }}
                                />                              
                                <CommandBarButton
                                    disabled={disabled}                        
                                    iconProps={{ iconName: "DisableUpdates" }}
                                    text="Exclude"
                                    onClick={async () => {
                                        this.state.excludedSchoolCodes.push(item.schoolCode);
                                        this.setState({ excludedSchoolCodes: this.state.excludedSchoolCodes })
                                        this.props.apiService.bulkWorkloadItemExclude(item.runBulkItemInstanceId, item.schoolCode);
                                        await this.props.dispatch(Actions.showMessageBar(`Excluded ${item.schoolCode}`, MessageBarType.success));
                                    }}
                                    />
                            </Fragment>
                        );
                    }
                    return (
                        <TableCell key={columnIndex} flex={column.flex}>
                            <div className={"flow-row"}>
                                {beginButton}
                                {failedActions}
                            </div>
                        </TableCell>
                    )
                    break;
                case "schoolCode":
                    return this.buildSchoolCell(item, 1);

                case "currentWorkloadInstanceId":
                    return this.buildWorkloadCell(item, 1);

                case "currentWorkloadInstanceRuntimeStatus":
                    return this.buildStatusCell(item, 1);
            }
        };

        return (
            <Table
                title={"Schools"}
                columns={columns}
                items={this.state.filteredBulkItems!}
                cellForColumn={cellForColumn}
                enableSort={true}>
                {this.buildBulkHeaderButtons()}
            </Table>
        );
    }

    private buildSchoolCell(instance: BulkWorkloadItem, flex: number) {
        return (
            <TableCell key={instance.schoolCode} to={`/cdb-admin/schools/${instance.tenantId}/${instance.schoolCode}`} enableLink={true} flex={flex}>
                <span>{instance.schoolCode}</span>
            </TableCell>
        );
    }

    private buildWorkloadCell(instance: BulkWorkloadItem, flex: number) {
        return (
            <TableCell key={instance.currentWorkloadInstanceId} to={`/cdb-admin/workloads/${instance.currentWorkloadInstanceId}`} enableLink={true} flex={flex}>
                <span>{instance.currentWorkloadInstanceId}</span>
            </TableCell>
        );
    }

    private buildStatusCell(instance: BulkWorkloadItem, flex: number) {
        return (
            <TableCell key={instance.currentWorkloadInstanceRuntimeStatus} flex={flex}>
                <span>{`${instance.currentWorkloadInstanceRuntimeStatus}${(instance.isExcluded) ?" - Excluded" : ""}`}</span>
            </TableCell>
        );
    }

    private filterBulkWorkloads(newValue: boolean) {

        if (newValue && this.state.bulkItems !== undefined) {
            this.setState({
                filteredBulkItems: this.state.bulkItems.filter(x => x.currentWorkloadInstanceRuntimeStatus !== "Completed")
            })
        }
        else {
            this.setState({
                filteredBulkItems: this.state.bulkItems
            })
        }
       
        this.setState({ bulkItemsHideCompleted: newValue })
    }

    private buildBulkHeaderButtons() {
        return (
            <Fragment>
                <Toggle
                    inlineLabel={true}
                    checked={this.state.bulkItemsHideCompleted}
                    onChange={(event, newValue) => {
                        this.filterBulkWorkloads(newValue!);
                    }}
                    role="switch"
                    label={"Hide Completed"}
                    />
               
            </Fragment>
        );
    }


    private buildDownloadForBulkButton() {

        if (this.state.instance?.bulkWorkloadName) {
            return (
                <CommandBarButton
                    text={"Download Summary Data"}
                    iconProps={{ iconName: "DownloadDocument" }}
                    onClick={async () => {
                        try {
                            let result;

                            result = await this.props.apiService.getGroundFloorDurableFunctionInstanceChildrenDownload(this.props.instanceId);

                            await openFileDownload(result)

                            this.props.dispatch(Actions.showMessageBar(`Downloading summary data`, MessageBarType.success));
                        }
                        catch (e) {
                            console.log(e);
                            this.props.dispatch(Actions.showMessageBar(`Error downloading summary data. If this problem persists please contact support. ${e.message}`, MessageBarType.error));
                        }
                    }} />
            );
        }
    }
    
    private buildRewindWorkloadInstanceButton = () => {
        
        if(!userAccessLevelIsAtLeast("admin",this.props.usersCdbAccessLevel))
        {
            return null;
        }
        
        if(this.state.instance?.runtimeStatus !== "Failed" || this.state.instance.playlistName || moment(this.state.instance.createdAt) < moment().subtract(12,'hour'))
        {
            return null;
        }
        
        return (
            <CommandBarButton
                iconProps={{ iconName: "Rewind" }}
                text="Rewind (preview)"
                onClick={async () => {

                    try
                    {
                        await this.props.apiService.rewindGroundFloorInstancePreview(this.props.instanceId)
                        await this.fetchInstanceAndRelatedData(this.props.instanceId);
                        await this.props.dispatch(Actions.showMessageBar("Successfully rewound workload",MessageBarType.success));
                    }
                    catch(e)
                    {
                        await this.props.dispatch(Actions.showMessageBar(`Failed to rewind workload : ${e.message}`,MessageBarType.error));
                    }
                }}
            />
        );
    }

    render() {
        
        if(this.state.instance && this.state.indexedSchools)
        {
            const title = unfriendlyDurableFunctionInstanceName(this.state.instance);
            
            return (
                <Fragment>
                    <SchoolPageTitle tenantId={this.state.instance?.tenantId} schoolCode={this.state.instance?.schoolCode} title={title} />
                    <AdminSchoolMenu tenantId={this.state.instance?.tenantId} schoolCode={this.state.instance?.schoolCode} />
                    <div className={"flex-column"}>
                        <GroundFloorDurableFunctionInstanceList title={title} enableRowLinks={false} indexedSchools={this.state.indexedSchools} instances={[this.state.instance]} headerExtra={this.buildHeaderButtons()} />
                        {this.buildOutputViewer("Output",this.state.instance?.Output,"showingOutput")}
                        {this.buildBulkItemTable()}
                        {this.state.instanceIsPlaylistOrBulkWorkload ? <GroundFloorDurableFunctionInstanceList title={"Workloads"} enableRowLinks={true} openRowLinksInNewTab={true} instances={this.state.childWorkloadInstances!} indexedSchools={this.state.indexedSchools} headerExtra={this.buildDownloadForBulkButton() } /> : <WorkUnitInvocationLogList tenantId={this.state.instance.input.tenantId} schoolCode={this.state.instance.schoolCode} title={"Work Units"} workUnitInvocationLogs={this.state.instance.workUnitInvocations} enableLinks={true} />}
                        <DurableFunctionHistoryEventList workloadInstance={this.state.instance} durableFunctionHistoryEvents={this.state.instanceHistory}>
                            {this.buildRewindWorkloadInstanceButton()}
                        </DurableFunctionHistoryEventList>

                        <Dialog
                            hidden={!this.state.showTerminateDialog}
                            onDismiss={() => {}}
                            dialogContentProps={{
                                type: DialogType.normal,
                                title: 'Terminate',
                                subText: `Terminate ${unfriendlyDurableFunctionInstanceName(this.state.instance)} at ${unfriendlySchoolName(this.state.instance)}?`
                            }}
                            modalProps={{
                                isBlocking: false,
                                styles: { main: { maxWidth: 600 } },
                            }}
                        >
                            <DialogFooter>
                                <PrimaryButton onClick={this.terminateInstance} text={"Terminate"}/>
                                <DefaultButton onClick={() => {
                                    this.setState({
                                        showTerminateDialog: false,
                                    });
                                }} text="Cancel" />
                            </DialogFooter>
                        </Dialog>

                    </div>
                </Fragment>
            );
        }
        
        return null;
    }
}

export default connect(
    (state: IRootState,ownProps: any): GroundFloorDurableFunctionInstancePageProps => {
        
        const instanceId = ownProps.match.params.instanceId;
        
        return {
            usersCdbAccessLevel: state.auth.usersCdbAccessLevel,
            instanceId: instanceId,
            apiService: state.auth.apiService,
            history: ownProps.history
        };
    }
)(GroundFloorDurableFunctionInstancePage);